import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

export interface CraftingLogSummary {
  logId: string;
  userId: string;
  recipeId: string | null;
  recipeType: string;
  recipeName: string;
  inputsConsumed: Array<{ itemName: string; quantity: number }>;
  outputsCreated: Array<{ itemName: string; quantity: number }>;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface UseCraftingLogOptions {
  userId?: string;
  type?: string;
  admin?: boolean;
}

interface UseCraftingLogReturn {
  entries: CraftingLogSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useCraftingLog(options: UseCraftingLogOptions = {}): UseCraftingLogReturn {
  const [entries, setEntries] = useState<CraftingLogSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const fetchIdRef = useRef(0);

  const fetchLog = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setError(null);
        setLoading(true);
        const params: Record<string, string> = {};
        if (options.userId) params.userId = options.userId;
        if (options.type) params.type = options.type;
        params.limit = String(limit);
        params.offset = String(newOffset);

        const path = options.admin ? '/arches/crafting/admin/log' : '/arches/crafting/log';
        const { data } = await api.get<{ entries: CraftingLogSummary[]; total: number }>(path, {
          params,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setEntries((prev) => [...prev, ...data.entries]);
        } else {
          setEntries(data.entries);
        }
        setTotal(data.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load crafting log');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.userId, options.type, options.admin],
  );

  useEffect(() => {
    fetchLog(0);
  }, [fetchLog]);

  const refresh = useCallback(() => fetchLog(0), [fetchLog]);
  const loadMore = useCallback(() => {
    if (offset + limit < total) {
      fetchLog(offset + limit, true);
    }
  }, [fetchLog, offset, total]);

  return {
    entries,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
